import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Badge,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  ListAlt,
  AddBox,
  LocalShipping,
  Business,
  MyLocation,
  People,
  Person,
  Logout,
  VpnKey,
  Visibility,
  VisibilityOff,
  Backup,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  MenuBook,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Receipt,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { authAPI, staffNotificationsAPI } from '../services/api';
import { fetchCompanyInfo } from '../services/companyInfo';

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api\/?$/, '');

// 한국어 TTS — 음성 목록 로드 대기 + ko 보이스 선택. cancel()을 speak 직전에 호출하지 않음(취소 버그 회피).
function speakKo(text) {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;
    const utter = () => {
      try {
        const u = new window.SpeechSynthesisUtterance(text);
        u.lang = 'ko-KR'; u.rate = 1.0; u.pitch = 1.0;
        const vs = synth.getVoices() || [];
        const ko = vs.find((v) => (v.lang || '').toLowerCase().startsWith('ko'));
        if (ko) u.voice = ko;
        try { synth.resume(); } catch (e) { /* ignore */ }
        synth.speak(u);
      } catch (e) { /* ignore */ }
    };
    if ((synth.getVoices() || []).length === 0) {
      let done = false;
      const once = () => { if (!done) { done = true; utter(); } };
      try { synth.onvoiceschanged = once; } catch (e) { /* ignore */ }
      setTimeout(once, 300);
    } else {
      utter();
    }
  } catch (e) { /* ignore */ }
}

// 최초 사용자 제스처에 TTS 잠금 해제 (브라우저 autoplay 정책상 제스처 전엔 speak가 무시됨)
let _ttsUnlocked = false;
function primeTtsOnce() {
  if (_ttsUnlocked) return;
  const prime = () => {
    _ttsUnlocked = true;
    try { window.speechSynthesis && window.speechSynthesis.speak(new window.SpeechSynthesisUtterance('')); } catch (e) { /* ignore */ }
    window.removeEventListener('pointerdown', prime);
    window.removeEventListener('keydown', prime);
    window.removeEventListener('touchstart', prime);
  };
  window.addEventListener('pointerdown', prime);
  window.addEventListener('keydown', prime);
  window.addEventListener('touchstart', prime);
}

const drawerWidth = 200;
const collapsedWidth = 60;

function Layout({ onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // 비밀번호 변경 다이얼로그
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [pushSnack, setPushSnack] = useState({ open: false, title: '', body: '' });
  const [company, setCompany] = useState({ company_name: '', company_phone: '', company_address: '' });

  useEffect(() => {
    fetchCompanyInfo().then(setCompany).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const res = await staffNotificationsAPI.getUnreadCount();
        if (mounted) setUnreadTotal(res.data.total_unread || 0);
      } catch (err) {
        // 조용히 무시
      }
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 30000);
    return () => { mounted = false; clearInterval(timer); };
  }, [location.pathname]);

  // 브라우저 알림 권한 요청 + Socket.IO로 직원 푸시 수신
  useEffect(() => {
    primeTtsOnce();
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      console.log('[Layout Socket] 연결됨, admin-room 입장');
      socket.emit('join-admin');
    });
    socket.on('connect_error', (err) => {
      console.warn('[Layout Socket] 연결 오류:', err.message);
    });
    const showPush = (title, body) => {
      setPushSnack({ open: true, title: title || '알림', body: body || '' });
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification(title || '알림', { body: body || '' }); } catch (e) { /* ignore */ }
      }
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      } catch (e) { /* ignore */ }
      // TTS — "써니왔어! {title}" (emoji 제거, 80자 제한)
      try {
        const ttsOn = localStorage.getItem('ttsEnabled');
        if (ttsOn === null || ttsOn === '1') {
          const cleanTitle = String(title || '').replace(/[\p{Extended_Pictographic}\p{Emoji_Component}]/gu, '').replace(/\s+/g, ' ').trim();
          const phrase = cleanTitle ? `써니왔어! ${cleanTitle}`.slice(0, 80) : '써니왔어!';
          speakKo(phrase);
        }
      } catch (e) { /* ignore */ }
      staffNotificationsAPI.getUnreadCount()
        .then((res) => setUnreadTotal(res.data.total_unread || 0))
        .catch(() => {});
    };

    socket.on('staff-push-notification', (data) => {
      console.log('[Layout Socket] staff-push-notification 수신:', data);
      showPush(data.title, data.body);
    });

    socket.on('new-customer-order', (data) => {
      console.log('[Layout Socket] new-customer-order 수신:', data);
      showPush('📦 새 배차 신청', data.message || `${data.origin || ''} → ${data.destination || ''}`);
    });

    socket.on('new-application', (data) => {
      console.log('[Layout Socket] new-application 수신:', data);
      showPush('🚚 새 배차 신청', data.message || '운전자가 배차를 신청했습니다.');
    });

    socket.on('customer-price-response', (data) => {
      console.log('[Layout Socket] customer-price-response 수신:', data);
      const actionLabel = {
        accepted: '금액 수락',
        hold: '금액 보류 (재제시 요청)',
        cancelled: '주문 취소'
      }[data.customerAction] || '응답';
      showPush(`📬 고객 응답: ${actionLabel}`, `${data.orderNumber || ''}`);
    });

    return () => { socket.disconnect(); };
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isManager = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  const menuItems = [
    { text: '대시보드', path: '/', icon: <Dashboard /> },
    { text: '주문 관리', path: '/orders', icon: <ListAlt /> },
    { text: '주문 생성', path: '/orders/new', icon: <AddBox /> },
    { text: '운전자 관리', path: '/drivers', icon: <LocalShipping /> },
    { text: '고객 관리', path: '/customers', icon: <Business /> },
    { text: '거래실적', path: '/transactions', icon: <Receipt /> },
    { text: '실시간 추적', path: '/tracking', icon: <MyLocation /> },
    {
      text: '알림 게시판',
      path: '/notifications',
      icon: (
        <Badge badgeContent={unreadTotal} color="error" overlap="circular">
          <NotificationsIcon />
        </Badge>
      ),
    },
    {
      text: '사용 설명서',
      path: '/manual',
      icon: <MenuBook />,
    },
  ];

  // 관리자 메뉴 (manager 이상)
  const managerMenuItems = [
    { text: '직원 관리', path: '/staff', icon: <People /> },
  ];

  // Admin 전용 메뉴
  const adminMenuItems = [
    { text: '이벤트 로그', path: '/event-logs', icon: <HistoryIcon /> },
    { text: '데이터 백업', path: '/backup', icon: <Backup /> },
    { text: '설정', path: '/settings', icon: <SettingsIcon /> },
  ];

  const getRoleText = (role) => {
    const roleMap = {
      super_admin: '최고관리자',
      admin: '최고관리자',
      manager: '관리자',
      operator: '직원',
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      admin: '#d32f2f',
      manager: '#ed6c02',
      operator: '#1976d2',
    };
    return colorMap[role] || '#1976d2';
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePasswordChangeOpen = () => {
    setPasswordDialogOpen(true);
    handleProfileMenuClose();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('모든 필드를 입력하세요.');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 4) {
      alert('새 비밀번호는 4자 이상이어야 합니다.');
      return;
    }

    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      alert('비밀번호가 변경되었습니다.');
      setPasswordDialogOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    }
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    onLogout();
  };

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  const drawerContent = (
    <>
      <List>
        {menuItems.map((item) => (
          <Tooltip key={item.text} title={collapsed ? item.text : ''} placement="right">
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileDrawerOpen(false);
              }}
              selected={location.pathname === item.path}
              sx={{
                px: collapsed ? 1.5 : 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                '&.Mui-selected': { backgroundColor: '#e3f2fd', borderRight: '3px solid #1976d2' },
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </Tooltip>
        ))}

        {isManager && (
          <>
            <Divider sx={{ my: 1 }} />
            {!collapsed && (
              <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
                관리자 메뉴
              </Typography>
            )}
            {managerMenuItems.map((item) => (
              <Tooltip key={item.text} title={collapsed ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileDrawerOpen(false); }}
                  selected={location.pathname === item.path}
                  sx={{
                    px: collapsed ? 1.5 : 2,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#fff3e0', borderRight: '3px solid #ed6c02' },
                    '&:hover': { backgroundColor: '#fff8e1' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center', color: '#ed6c02' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.text} sx={{ '& .MuiTypography-root': { fontWeight: 500 } }} />}
                </ListItemButton>
              </Tooltip>
            ))}
          </>
        )}

        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            {!collapsed && (
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#d32f2f', display: 'block', fontWeight: 600 }}>
                시스템 관리
              </Typography>
            )}
            {adminMenuItems.map((item) => (
              <Tooltip key={item.text} title={collapsed ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileDrawerOpen(false); }}
                  selected={location.pathname === item.path}
                  sx={{
                    px: collapsed ? 1.5 : 2,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': { backgroundColor: '#ffebee', borderRight: '3px solid #d32f2f' },
                    '&:hover': { backgroundColor: '#ffebee' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center', color: '#d32f2f' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.text} sx={{ '& .MuiTypography-root': { fontWeight: 500, color: '#d32f2f' } }} />}
                </ListItemButton>
              </Tooltip>
            ))}
          </>
        )}
      </List>

    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* 햄버거 버튼: 모바일=드로어 열기, 데스크톱=사이드바 접기/펼치기 */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            🚚 {company.company_name || ''} 배차 시스템
          </Typography>
          
          {user && (
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={getRoleText(user.role)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              <IconButton color="inherit" onClick={handleProfileMenuOpen}>
                <Avatar sx={{ bgcolor: getRoleColor(user.role), width: 36, height: 36 }}>
                  {user.name?.charAt(0) || <Person />}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
              >
                <Box sx={{ px: 2, py: 1, minWidth: 180 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.username}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handlePasswordChangeOpen}>
                  <ListItemIcon>
                    <VpnKey fontSize="small" />
                  </ListItemIcon>
                  비밀번호 변경
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  로그아웃
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* 모바일: temporary 드로어 */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            marginTop: '64px',
            borderRight: '1px solid #e0e0e0',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* 데스크톱: permanent 드로어 (접기/펼치기 가능) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
            marginTop: '64px',
            borderRight: '1px solid #e0e0e0',
            overflowX: 'hidden',
            transition: 'width 0.2s ease',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginTop: '64px',
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease',
        }}
      >
        <Box sx={{ flexGrow: 1, p: 2 }}>
          <Outlet />
        </Box>
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 3,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {company.company_name || ''}
            {company.company_phone ? ` | 대표전화 ${company.company_phone}` : ''}
            {company.company_address ? ` | ${company.company_address}` : ''}
          </Typography>
        </Box>
      </Box>

      {/* 운전자 이벤트 푸시 스낵바 */}
      <Snackbar
        open={pushSnack.open}
        autoHideDuration={6000}
        onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
          severity="info"
          variant="filled"
          sx={{ minWidth: 300, cursor: 'pointer' }}
          onClick={() => { navigate('/notifications'); setPushSnack((s) => ({ ...s, open: false })); }}
        >
          <Typography variant="subtitle2" fontWeight={700}>{pushSnack.title}</Typography>
          <Typography variant="body2">{pushSnack.body}</Typography>
        </Alert>
      </Snackbar>

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>비밀번호 변경</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="현재 비밀번호"
              type={showPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="새 비밀번호"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="새 비밀번호 확인"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>취소</Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary">
            변경
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Layout;
